import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import RightModalLayout from '../../../layouts/Modal/RightModalLayout';
import SearchMC from '../../../components/SearchMC';
import ButtonMC from '../../../components/ButtonMC';
import CustomersModelMC from '../../../models/CustomersModelMc';
import { openSidebar, closeSidebar } from '../../Orders/services/orderSidebarService';

const SelectCustomerModal = ({ onSelect }) => {
  const { t } = useTranslation('Quotes');
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleClose = useCallback(() => {
    closeSidebar(dispatch, 'SelectCustomerModal');
  }, [dispatch]);

  const runSearch = useCallback(async (text) => {
    if (!text?.trim()) {
      setCustomers([]);
      return;
    }

    setLoading(true);
    try {
      const response = await CustomersModelMC.search({ text: text.trim(), with_custom_fields: true });
      setCustomers(response?.customers || []);
    } catch (error) {
      console.error(error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSelect = async (customer) => {
    try {
      if (customer.customer_related_data || customer.customer_related_data_attributes) {
        onSelect?.(customer);
        return;
      }
      setLoading(true);
      const fullRes = await CustomersModelMC.get({}, { path: `/api/v1/customers/${customer.id}/edit` });
      const fullCustomer = fullRes?.customer || fullRes || customer;
      onSelect?.(fullCustomer);
    } catch (error) {
      console.error(error);
      onSelect?.(customer);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = () => {
    openSidebar(dispatch, {
      componentKey: 'ManageClientForOrderModal',
      props: {
        customer: null,
        action: 'new',
        onSaveSuccess: (savedCustomer) => {
          onSelect?.(savedCustomer);
        }
      }
    });
  };

  return (
    <RightModalLayout
      closeAction={handleClose}
      title={t('quote.selectCustomer')}
      showBackButton
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <SearchMC
          placeholder={t('quote.searchCustomerPlaceholder')}
          defaultValue=""
          debounceTime={500}
          onChange={(value) => {
            setSearchText(value);
            runSearch(value);
          }}
        />

        <ButtonMC
          variant="SECONDARY"
          size="small"
          text={t('quote.createCustomer')}
          onClick={handleCreateCustomer}
        />

        {loading && (
          <p className="tw-text-sm tw-text-gray-500">{t('quote.searching')}</p>
        )}

        {!loading && searchText && customers.length === 0 && (
          <p className="tw-text-sm tw-text-gray-400">{t('quote.noCustomersFound')}</p>
        )}

        <div className="tw-flex tw-flex-col tw-gap-2">
          {customers.map((customer) => {
            const name = customer.full_name
              || [customer.first_name, customer.last_name].filter(Boolean).join(' ');

            return (
              <button
                key={customer.id}
                type="button"
                className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-2 tw-text-left hover:tw-bg-gray-50"
                onClick={() => handleSelect(customer)}
              >
                <p className="tw-font-medium tw-text-sm">{name}</p>
                <p className="tw-text-xs tw-text-gray-500">
                  {[customer.phone, customer.email].filter(Boolean).join(' · ')}
                </p>
              </button>
            );
          })}
        </div>
      </div>
    </RightModalLayout>
  );
};

export default SelectCustomerModal;
